import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { DUNGEON_RESOURCES } from '../../data/dungeon/resources';

/**
 * MaterialChip - A reusable component for displaying dungeon materials
 * Shows tooltip with material info, rarity, and drop sources on hover
 *
 * Usage:
 *   <MaterialChip materialId="bone" amount={100} />
 *   <MaterialChip materialId="essence" amount={50} showName />
 *   <MaterialChip materialId="dragonScale" /> // Just icon
 *   <MaterialChip materialId="bone" amount={100} currentAmount={50} /> // Shows have/need
 */

// Rarity colors matching the game's design system
const RARITY_COLORS = {
  common: { bg: 'rgba(180, 180, 180, 0.15)', border: 'rgba(180, 180, 180, 0.4)', text: '#b8b8b8' },
  uncommon: { bg: 'rgba(80, 200, 80, 0.15)', border: 'rgba(80, 200, 80, 0.4)', text: '#50c850' },
  rare: { bg: 'rgba(80, 140, 220, 0.15)', border: 'rgba(80, 140, 220, 0.4)', text: '#508cdc' },
  epic: { bg: 'rgba(160, 80, 200, 0.15)', border: 'rgba(160, 80, 200, 0.4)', text: '#a050c8' },
  legendary: { bg: 'rgba(255, 170, 0, 0.15)', border: 'rgba(255, 170, 0, 0.4)', text: '#ffaa00' },
};

// Drop source hints by material
const DROP_SOURCES = {
  // Common
  bone: 'Dropped by skeletons and undead enemies',
  leather: 'Dropped by beasts and humanoid enemies',
  cloth: 'Dropped by mages and dungeon dwellers',
  ironOre: 'Found in dungeon walls and mining nodes',
  leatherScraps: 'Dropped by weak beasts',
  wolfFang: 'Dropped by wolves and dire wolves',
  fur: 'Dropped by furry beasts',
  rustyScrap: 'Found in ancient ruins',
  wood: 'Found in dungeon structures',
  // Uncommon
  essence: 'Dropped by magical enemies',
  crystal: 'Found in treasure chests',
  rune: 'Dropped by elite enemies',
  orcTusk: 'Dropped by orc warriors',
  darkSteel: 'Dropped by dark knights',
  ectoplasm: 'Dropped by wraiths and ghosts',
  necroticEssence: 'Dropped by powerful undead',
  // Rare
  soulShard: 'Dropped by boss monsters',
  dragonScale: 'Dropped by dragon-type enemies',
  voidFragment: 'Dropped in Tier 4+ dungeons',
  demonHorn: 'Dropped by demon enemies',
  sulfur: 'Found in volcanic dungeons',
  vampireFang: 'Dropped by vampires',
  bloodVial: 'Dropped by vampire lords',
  ogreHeart: 'Dropped by ogre bosses',
  thickHide: 'Dropped by boss monsters',
  lichPhylactery: 'Dropped by lich bosses',
  dragonHeart: 'Dropped by dragon bosses',
  ancientBone: 'Found in ancient ruins',
  // Epic
  divineEssence: 'Dropped in Tier 4+ dungeons',
  eldritchHeart: 'Dropped by eldritch horrors',
  voidEssence: 'Dropped in Void Citadel',
  cosmicFragment: 'Dropped in Tier 5 dungeons',
  starMetal: 'Dropped in Tier 5 dungeons',
  // Legendary
  cosmicOre: 'Dropped by final bosses',
  eterniumIngot: 'Dropped by legendary bosses',
};

function MaterialChip({
  materialId,
  amount,
  currentAmount,  // Optional: player's current amount (for showing have/need)
  showName = false,
  showTooltip = true,
  className = '',
  canAfford = true,  // For cost displays
  size = 'normal',   // 'small', 'normal', 'large'
}) {
  const [tooltip, setTooltip] = useState(null);

  const material = DUNGEON_RESOURCES[materialId];

  if (!material) {
    return <span className="mc-unknown">❓{materialId}</span>;
  }

  const handleMouseEnter = (e) => {
    if (!showTooltip) return;
    setTooltip({
      x: e.clientX,
      y: e.clientY,
    });
  };

  const handleMouseMove = (e) => {
    if (!tooltip) return;
    setTooltip({
      x: e.clientX,
      y: e.clientY,
    });
  };

  const handleMouseLeave = () => {
    setTooltip(null);
  };

  const rarityStyle = RARITY_COLORS[material.rarity] || RARITY_COLORS.common;
  const sizeClass = size === 'small' ? 'mc-small' : size === 'large' ? 'mc-large' : '';
  const isCostDisplay = currentAmount !== undefined;
  const affordClass = isCostDisplay ? (canAfford ? 'mc-can-afford' : 'mc-cannot-afford') : '';

  return (
    <>
      <span
        className={`material-chip ${sizeClass} ${affordClass} ${className}`}
        style={{
          '--mc-bg': rarityStyle.bg,
          '--mc-border': rarityStyle.border,
        }}
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <span className="mc-icon">{material.icon}</span>
        {amount !== undefined && <span className="mc-amount">{formatAmount(amount)}</span>}
        {showName && <span className="mc-name">{material.name}</span>}
      </span>

      {tooltip && ReactDOM.createPortal(
        <MaterialTooltip
          material={material}
          amount={amount}
          currentAmount={currentAmount}
          canAfford={canAfford}
          x={tooltip.x}
          y={tooltip.y}
        />,
        document.body
      )}
    </>
  );
}

/**
 * MaterialTooltip - The popup that shows material info
 */
function MaterialTooltip({ material, amount, currentAmount, canAfford, x, y }) {
  const tooltipWidth = 280;
  const tooltipHeight = 200;

  let left = x + 15;
  let top = y - 10;

  if (left + tooltipWidth > window.innerWidth - 10) {
    left = x - tooltipWidth - 15;
  }
  if (left < 10) left = 10;
  if (top + tooltipHeight > window.innerHeight - 10) {
    top = window.innerHeight - tooltipHeight - 10;
  }
  if (top < 10) top = 10;

  const showCostComparison = amount !== undefined && currentAmount !== undefined;
  const rarityStyle = RARITY_COLORS[material.rarity] || RARITY_COLORS.common;
  const dropSource = DROP_SOURCES[material.id] || 'Obtained from dungeons';

  return (
    <div
      className="material-tooltip"
      style={{ left, top }}
    >
      <div className="mt-header" style={{ '--rarity-color': rarityStyle.text }}>
        <span className="mt-icon">{material.icon}</span>
        <div className="mt-info">
          <span className="mt-name">{material.name}</span>
          <span className="mt-rarity" style={{ color: rarityStyle.text }}>
            {material.rarity.charAt(0).toUpperCase() + material.rarity.slice(1)}
          </span>
        </div>
      </div>

      {/* Cost comparison */}
      {showCostComparison && (
        <div className={`mt-cost-comparison ${canAfford ? 'can-afford' : 'cannot-afford'}`}>
          <div className="mt-cost-row">
            <span className="mt-cost-label">Have</span>
            <span className="mt-cost-value">{formatAmount(currentAmount)}</span>
          </div>
          <span className="mt-cost-sep">/</span>
          <div className="mt-cost-row">
            <span className="mt-cost-label">Need</span>
            <span className="mt-cost-value">{formatAmount(amount)}</span>
          </div>
          <span className="mt-cost-status">
            {canAfford ? '✓' : `−${formatAmount(amount - currentAmount)}`}
          </span>
        </div>
      )}

      <p className="mt-desc">{material.description}</p>

      {/* Drop source */}
      <div className="mt-source">
        <span className="mt-source-icon">🎯</span>
        <span className="mt-source-text">{dropSource}</span>
      </div>

      {/* Stack size */}
      <div className="mt-stack">
        <span className="mt-stack-label">Max Stack:</span>
        <span className="mt-stack-value">{material.stackSize}</span>
      </div>
    </div>
  );
}

/**
 * Format large numbers nicely
 */
function formatAmount(num) {
  if (num === undefined || num === null) return '';
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 10000) return (num / 1000).toFixed(0) + 'K';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return Math.floor(num).toString();
}

/**
 * MaterialCost - Convenience wrapper for displaying costs
 */
export function MaterialCost({ materialId, amount, available = 0 }) {
  const canAfford = available >= amount;
  return (
    <MaterialChip
      materialId={materialId}
      amount={amount}
      currentAmount={available}
      canAfford={canAfford}
      size="small"
    />
  );
}

export default MaterialChip;
